import { SecureFieldsProps } from '@msc-b2c/interfaces/types';
import styles from './paymentsection/paymentsection.module.scss';
import SecureField from '../paymentbox/components/SecureField/SecureField';
import { setSecureFieldIframeTitle } from '../paymentbox/components/SecureField/secureFieldA11yUtils';
import PaymentSubmit from '../paymentbox/components/PaymentSubmit/PaymentSubmit';
import clsx from 'clsx';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button } from '../button/Button';
import { Image } from '../image/Image';
import { useSearchParams } from 'react-router-dom';
import {
  closeForceLoader,
  closeLoader,
  errorModalOpen,
  getActiveSession,
  getErrorMessage,
  is3dsPaymentSuccess,
  isChain2,
  isMyMscArea,
  isProduction,
  mscGet,
  mscRemove,
  mscSet,
  OnPriceToBookOptions,
  openLoader,
  payment3dsFlow,
  PaymentContext,
  RootState,
  store,
  useGetBookingQuery,
  myAreaFinalizeBookingPayment,
  isElementVisible,
} from '@msc-b2c/shared-utils';
import { handleFormEnterSubmit } from '@msc-b2c/shared-utils/keyboardUtils';
import { useDispatch, useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';

const initalInputStyle =
  'font-size: 100%; border-radius: 0; -webkit-appearance: none; padding: 0; outline: none';
const initalCssClass = {
  'secure-field': true,
  'secure-field__input ': true,
  'secure-field__base ': true,
  'secure-field__has-actions ': true,
  'secure-field__has-card-icon': true,
  'secure-field__has-error': false,
  'secure-field__is-recognized': false,
};
const SecureFields = ({
  paymentType,
  config,
  onSecureFieldsError,
  secureFields,
}: SecureFieldsProps) => {
  const bookingNumber = getActiveSession().bookingNumber ?? '';
  const { data: bookingData } = useGetBookingQuery(bookingNumber || skipToken, {
    selectFromResult: ({ data }) => ({ data }),
  });
  const [loading, setLoading] = useState(false);
  const [expm, setExpm] = useState('');
  const [expy, setExpy] = useState('');
  const [cardContainerClassNames, setCardContainerClassNames] = useState(initalCssClass);
  const [cvvContainerClassNames, setCvvContainerClassNames] = useState(initalCssClass);
  const [expiryDate, setExpiryDate] = useState('');
  const [areTermsAgreed, setAreTermsAgreed] = useState(false);
  const secureFieldsWindow = window?.SecureFields;
  const [searchParams, setSearchParams] = useSearchParams();
  const status3D = searchParams.get('status_3d') && searchParams.get('status_3d') === 'Y';
  const uppTransactionId = searchParams.get('uppTransactionId') ?? '';
  const [expiryValid, setExpiryValid] = useState(true);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const { is3dsFlow, is3dsBookingSuccess } = useSelector((state: RootState) => state.payment);
  const userEmailInput = useSelector((state: RootState) => state.myAreaCart?.loggedUserEmailInput);
  const initializedTransactionIdRef = useRef<string | null>(null);

  const initSecureFields = () => {
    if (initializedTransactionIdRef.current === config.transactionId) {
      return;
    }
    initializedTransactionIdRef.current = config.transactionId;
    secureFields.init(config.transactionId, config.fields);
  };

  const { onFinalizePayment, onPriceToBook } = useContext(PaymentContext);

  const refreshPricesAfterPaymentFailure = () => {
    const options: OnPriceToBookOptions = { revalidateCouponAfterPaymentFailure: true };
    onPriceToBook(options);
  };

  const handleCheck = () => setAreTermsAgreed((prevTerms) => !prevTerms);

  const clearExpiryField = () => {
    setExpiryDate('');
    setExpm('');
    setExpy('');
    setExpiryValid(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove any non-digit characters
    value = value.replace(/\D/g, '');

    setExpiryValid(true);

    // Format as MM / YY
    if (value.length <= 2) {
      // Allow typing the month (first two digits)
      setExpiryDate(value);
    } else if (value.length <= 4) {
      // Add a slash after the month and limit to MM / YY
      setExpiryDate(`${value.substring(0, 2)} / ${value.substring(2, 4)}`);
      setExpm(value.substring(0, 2));
      setExpy(value.substring(2, 4));
    }
  };

  // Handle backspace and avoid removing the separator directly
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      // If backspace is pressed and we're at the position after the `/`, adjust accordingly
      if (expiryDate.length === 5 && expiryDate.endsWith(' ')) {
        setExpiryDate(expiryDate.slice(0, -3)); // Remove " / " as a whole
        e.preventDefault();
      }
    }
  };

  useEffect(() => {
    initializedTransactionIdRef.current = null;
  }, [secureFields]);

  useEffect(() => {
    if (secureFields && secureFieldsWindow && !status3D && config?.transactionId) {
      initSecureFields();
    }
  }, [secureFieldsWindow, status3D, secureFields, config?.transactionId]);

  const removeSpecificQueryParams = (paramsToRemove: string[]): void => {
    // Remove specific parameters
    paramsToRemove.forEach((param) => searchParams.delete(param));
    setSearchParams(searchParams, { replace: true });
  };

  /* Logged user pax info */
  const loggedUserId = getActiveSession().passengerId;
  //find in booking the name of the passenger with the loggedUserId
  const loggedUserPax = bookingData?.passengers?.find((pax) => pax.id === loggedUserId);
  // leadPax (isLeadPassenger=true) is always present in a valid booking and typically has
  // full contact data — use it as fallback when loggedUserPax is unavailable.
  const leadPax = bookingData?.passengers?.find((pax) => pax.isLeadPassenger);
  const resolvedPax = loggedUserPax ?? leadPax;

  //If OTP is ok then call INT146 - Finalize payment api // TODO move to a separate function with a condition to adeguate the prepaid purchase
  useEffect(() => {
    if (is3dsFlow && uppTransactionId) {
      const datatrans3dPaymentType = mscGet<string>('session', 'paymentType');

      if (datatrans3dPaymentType === paymentType) {
        // remove params from URL
        removeSpecificQueryParams(['status_3d', 'uppTransactionId']);
        openLoader('.loader');
        setLoading(true);
        onSecureFieldsError('otpLoading', true);
        setLoading(true);
        onFinalizePayment(
          { transactionId: uppTransactionId, type: paymentType },
          (res) => {
            dispatch(is3dsPaymentSuccess(true));
            onSecureFieldsError('otpLoading', false);
          },
          (error) => {
            // isMyMscArea() alone isn't enough to decide whether to show the generic error modal:
            // it was muted to avoid a duplicate modal on top of the coupon-specific error UI in the
            // prepaid cart-checkout flow (PrepaidPayment.tsx dispatches setPaymentCouponErrorCode/
            // setErrorManagerCode before calling onError). The deposit/full booking-payment flow
            // (PaymentWrapper.tsx) has no such alternate error UI — it only dispatches
            // myAreaFinalizeBookingPayment(...with _warnings...) — so we check that to decide.
            const finalizeBookingPaymentWarnings =
              store.getState().myCruise?.finalizeBookingPayment?._warnings;
            const shouldShowGenericErrorModal = !isMyMscArea() || !!finalizeBookingPaymentWarnings;
            closeLoader('.loader');
            if (shouldShowGenericErrorModal) {
              dispatch(errorModalOpen(true));
            }
            dispatch(is3dsPaymentSuccess(false));
            dispatch(payment3dsFlow(false));
            clearExpiryField();
            refreshPricesAfterPaymentFailure();
            if (shouldShowGenericErrorModal) {
              onSecureFieldsError('datatransError', error.message);
            }
            onSecureFieldsError('otpLoading', false);
          },
          () => {
            closeLoader('.loader');
            setLoading(false);
          }
        );
      }
      // else: this SecureFields instance isn't the one that started this 3DS transaction
      // (another PaymentSection/paymentType mounted on the same page owns it, e.g. DEPOSIT vs FULL).
      // Do nothing — treating the mismatch as a failure caused a false error modal to appear
      // while the matching instance was still finalizing the payment in the background.
    } else if (!status3D && uppTransactionId) {
      removeSpecificQueryParams(['status_3d', 'uppTransactionId']);
      mscRemove('session', 'paymentType');
      mscRemove('session', 'paymentAmount');
      clearExpiryField();
      dispatch(payment3dsFlow(false));
      onSecureFieldsError('otpError', true);
      closeLoader('.loader');
      dispatch(errorModalOpen(true));
      refreshPricesAfterPaymentFailure();
    }
  }, [status3D, is3dsFlow, uppTransactionId]);

  useEffect(() => {
    if (secureFields) {
      secureFields.on('ready', () => {
        // Set styles manually as they're inside an iframe and out of the scope of the parent's stylesheets
        secureFields.setStyle('cardNumber', initalInputStyle);
        secureFields.setStyle('cvv', initalInputStyle);
        secureFields.setStyle('expiry', initalInputStyle);

        setSecureFieldIframeTitle(`card-number-${config.paymentType}`, config.labels.cardLabel);
        setSecureFieldIframeTitle(`cvv-number-${config.paymentType}`, config.labels.cvvLabel);

        setIsLoading(false);
      });

      // Set class names and icon when fields change
      secureFields.on('change', (data: { fields: { cardNumber: { paymentMethod: string } } }) => {
        const paymentMethod = data.fields.cardNumber.paymentMethod
          ? data.fields.cardNumber.paymentMethod
          : false;

        setCardContainerClassNames({
          ...cardContainerClassNames,
          'secure-field__is-recognized': !!paymentMethod,
          'secure-field__has-error': false,
        });
        setCvvContainerClassNames({
          ...cvvContainerClassNames,
          'secure-field__has-error': false,
        });
      });

      // Set error icon and class name on validate failure
      secureFields.on(
        'validate',
        (data: { fields: { cardNumber: { valid: boolean }; cvv: { valid: boolean } } }) => {
          if (!data.fields.cardNumber.valid) {
            setCardContainerClassNames({
              ...cardContainerClassNames,
              'secure-field__is-recognized': false,
              'secure-field__has-error': true,
            });
          }

          if (!data.fields.cvv.valid) {
            setCvvContainerClassNames({
              ...cvvContainerClassNames,
              'secure-field__has-error': true,
            });
          }
        }
      );

      // 1. SUCCESS FULL
      // 2. SUCCESS DEPOSIT
      // 3. SUCCESS BOOKING - FAIL PAYMENT
      // 5. FAIL
      // 6. DATATRANS ERROR -> Payment notification

      // TODO Add error case
      secureFields.on('error', (data: unknown) => {
        clearExpiryField();
        refreshPricesAfterPaymentFailure();
        if (data === 'Invalid transactionId') {
          // onPriceToBook (already called above) fetches new prices with new transactionId.
          // init will run when config.transactionId updates.
          return;
        }

        const message = getErrorMessage(data);
        closeLoader('.loader');
        dispatch(errorModalOpen(true));
        onSecureFieldsError('datatransError', message);
      });

      // Show transaction ID on success or transaction error message
      secureFields.on(
        'success',
        (data: { transactionId: string; redirect: string; error: string }) => {
          if (data.transactionId) {
            mscSet('session', 'paymentData', data);
            // Available options: FULL, DEPOSIT, HOLD

            // Create the finalize payment request body

            // Handle 3D-Secure scenario
            if (data.redirect?.trim()) {
              dispatch(myAreaFinalizeBookingPayment(null));
              dispatch(payment3dsFlow(true));

              mscSet('session', 'paymentType', paymentType);
              mscSet('session', 'paymentAmount', config.totalAmount);

              mscSet('session', 'myCruiseData', window.store.getState().myCruise, 365);

              mscSet('session', 'myCruiseData', window.store.getState().myCruise, 365);

              // Redirect to OTP page
              window.location.href = data.redirect;
            } else {
              // Proceed with payment API if there's no redirect or OTP is authorized
              openLoader('.loader');
              setLoading(true);

              onFinalizePayment(
                { transactionId: data.transactionId, type: paymentType },
                (_res) => {},
                (error) => {
                  clearExpiryField();
                  refreshPricesAfterPaymentFailure();
                  closeLoader('.loader');
                  dispatch(errorModalOpen(true));
                  onSecureFieldsError('datatransError', error.message);
                },
                () => {
                  setLoading(false);
                  closeLoader('.loader');
                }
              );
            }
          } else if (data.error) {
            // Handle error scenario
            clearExpiryField();
          }
        }
      );
    }
  }, [secureFields, status3D, is3dsFlow]);

  useEffect(() => {
    if (is3dsBookingSuccess) {
      closeForceLoader();
      window.location.href =
        (isProduction() || isChain2() ? '' : window.location.origin) +
        `${window.location.pathname}`;
    }
  }, [is3dsBookingSuccess]);

  useEffect(() => {
    if (is3dsFlow) return;
    loading ? openLoader('.loader') : closeLoader('.loader');
  }, [loading]);

  return !loading ? (
    <form
      onKeyDown={handleFormEnterSubmit}
      onSubmit={(e) => {
        e.preventDefault();

        if (!userEmailInput && isElementVisible('.cmp-sidesummary__email-section')) {
          return;
        }

        const rawExpiry = expiryDate.replace(/\D/g, '');
        if (rawExpiry.length >= 2) {
          const month = parseInt(rawExpiry.substring(0, 2), 10);
          if (month < 1 || month > 12) {
            setExpiryValid(false);
            return;
          }
        }
        secureFields.submit({
          expm,
          expy,
          '3D': {
            cardholder: {
              email: resolvedPax?.email ?? userEmailInput,
              // leadPax guarantees this is always populated for any valid booking.
              // `|| undefined` is a safety net for edge cases with empty strings only.
              cardholderName: `${resolvedPax?.name ?? ''} ${resolvedPax?.surname ?? ''}`,
              // homePhone is optional for 3DS: only send it when we actually have a phone number
              ...(resolvedPax?.phone
                ? {
                    homePhone: {
                      cc: resolvedPax?.phonePrefix,
                      subscriber: resolvedPax.phone,
                    },
                  }
                : undefined),
            },
          },
        });
      }}
    >
      <div className={styles.formInputs}>
        <div className={styles.formGroup}>
          {/* <!-- Card Number markup --> */}
          <SecureField
            amountType={config.paymentType || ''}
            fieldType="card"
            label={config.labels.cardLabel}
            customClass={clsx(cardContainerClassNames)}
            callback={() => secureFields?.focus(`cardNumber-number-${config.paymentType}`)}
          ></SecureField>
        </div>

        <div
          id="expiry-container"
          className={`secure-field secure-field__input secure-field__base secure-field__has-actions secure-field__has-card-icon ${
            styles.formGroup
          } ${!expiryValid ? 'secure-field__has-error' : ''}`}
        >
          <div className="secure-field--input-container">
            <input
              id="expiry"
              type="text"
              inputMode="numeric"
              maxLength={7}
              placeholder={config.labels.expirationLabel}
              className="secure-field--input"
              autoComplete="cc-exp"
              value={expiryDate}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          {/* <!-- CVV markup --> */}
          <SecureField
            amountType={config.paymentType || ''}
            fieldType="cvv"
            label={config.labels.cvvLabel}
            customClass={clsx(cvvContainerClassNames)}
            callback={() => secureFields?.focus(`cvv-number-${config.paymentType}`)}
          ></SecureField>
        </div>
        <Image
          src={config.cardImg || ''}
          alt="Card Icon"
          styleOption="secure-fields-card-image"
        />
      </div>
      <div className={styles.disclaimerContainer}>
        <span className={styles.disclaimer}>{config.labels.mandatorylabel}</span>
        <span className={styles.disclaimer}>{config.labels.disclaimer}</span>
      </div>

      <PaymentSubmit
        id={`${config.paymentType}-terms${config.labels.cardLabel.replace(/\s/g, '')}`}
        terms={config.labels.termsLabel}
        onCheck={handleCheck}
        checked={areTermsAgreed}
      >
        <Button
          id={`form-submit-${config.paymentType}`}
          buttonType={'submit'}
          text={config.labels.orderButtonLabel}
          accessibilityLabel={config.labels.orderButtonLabel}
          disabled={
            isLoading ||
            !areTermsAgreed ||
            (isElementVisible('.cmp-sidesummary__email-section') && !userEmailInput)
          }
          styleOption={'cmp-button__primary -extended'}
        />
      </PaymentSubmit>
    </form>
  ) : null;
};
export default SecureFields;
